import { Component, forwardRef, Input, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

/** What the field answers: an instant plus the explicit "no set time" mark (E4 RF-04, RF-05). */
export interface ScheduleMoment {
  dueAt: Date;
  untimed: boolean;
}

/**
 * Date + time + "sem horário", as ONE form control (SPEC-2026-10-crm-activities-cadence RF-04).
 * Created once and rendered by the next-action dialog, the task form, the conclusion dialog and
 * the postpone dialog, so "a date without a time of day" is entered the same way everywhere.
 *
 * The value is `{ dueAt, untimed }`: an untimed moment carries the local midnight of its day and
 * the API stores it at the company-local midnight (plan AD-05); a timed one carries the instant
 * the browser combined. The mark is a positive choice (RF-05): clearing the time does not make an
 * activity untimed — the box does.
 *
 * Also a `Validator`: the parent control is invalid while the date is missing (when required)
 * or while a timed moment has no time, so the forms around it refuse to submit a half moment.
 */
@Component({
  selector: 'app-schedule-moment-field',
  templateUrl: './schedule-moment-field.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ScheduleMomentFieldComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ScheduleMomentFieldComponent), multi: true },
  ],
})
export class ScheduleMomentFieldComponent implements ControlValueAccessor, Validator {

  /** Offer "sem horário". The conclusion dialog hides it: an interaction happened at a time. */
  @Input() allowUntimed = true;
  /** Cap the date — the conclusion dialog caps it at today (RF-07). */
  @Input() maxDate: Date | null = null;
  @Input() minDate: Date | null = null;
  /** Whether a date is required at all; the task form's deadline is optional. */
  @Input() required = true;
  /** Prefix of the three input ids, so two fields on one screen keep distinct labels. */
  @Input() idPrefix = 'moment';

  date: Date | null = null;
  time = '';
  untimed = false;
  disabled = false;
  touched = false;

  private onChange: (value: ScheduleMoment | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: ScheduleMoment | null | undefined): void {
    if (!value?.dueAt) {
      this.date = null;
      this.time = '';
      this.untimed = false;
      return;
    }

    const at = new Date(value.dueAt);
    this.date = isNaN(at.getTime()) ? null : at;
    this.untimed = this.allowUntimed && value.untimed === true;
    this.time = this.untimed || !this.date ? '' : ScheduleMomentFieldComponent.timeOf(this.date);
  }

  registerOnChange(fn: (value: ScheduleMoment | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.date) return this.required ? { momentRequired: true } : null;
    if (!this.untimed && !ScheduleMomentFieldComponent.isTime(this.time)) return { momentTimeRequired: true };
    return null;
  }

  /** The current answer, or null while incomplete. */
  get value(): ScheduleMoment | null {
    if (!this.date) return null;
    if (this.untimed) {
      const day = new Date(this.date);
      day.setHours(0, 0, 0, 0);
      return { dueAt: day, untimed: true };
    }
    if (!ScheduleMomentFieldComponent.isTime(this.time)) return null;
    return { dueAt: ScheduleMomentFieldComponent.combine(this.date, this.time), untimed: false };
  }

  get showDateError(): boolean {
    return this.touched && this.required && !this.date;
  }

  get showTimeError(): boolean {
    return this.touched && !!this.date && !this.untimed && !ScheduleMomentFieldComponent.isTime(this.time);
  }

  onDateChange(value: Date | null): void {
    this.date = value ?? null;
    this.emit();
  }

  onTimeChange(value: string): void {
    this.time = value ?? '';
    this.emit();
  }

  onUntimedChange(checked: boolean): void {
    this.untimed = checked === true;
    if (this.untimed) this.time = '';
    this.emit();
  }

  markTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  private emit(): void {
    this.onChange(this.value);
  }

  static isTime(value: string): boolean {
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(`${value ?? ''}`);
  }

  static timeOf(date: Date): string {
    return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
  }

  static combine(date: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const at = new Date(date);
    at.setHours(hours, minutes, 0, 0);
    return at;
  }
}
